import i18n from 'i18next';

import { activeLocale, formatTime } from './locale';

const pad = (v: number) => String(v).padStart(2, '0');

/** 8048 → "2:14:08" */
export function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${pad(m)}:${pad(s % 60)}`;
}

type MinutesOptions = { signed?: boolean };

/** 580 → "9h 40m" · 50 → "50m" · 0 → "0h 00m" · signed: +1h 40m / -50m / 0m */
export function formatMinutes(minutes: number, { signed = false }: MinutesOptions = {}): string {
  const abs = Math.abs(Math.round(minutes));
  if (signed && abs === 0) return '0m';
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const body = h === 0 && (signed || abs > 0) ? `${m}m` : `${h}h ${pad(m)}m`;
  if (!signed) return body;
  return `${minutes > 0 ? '+' : '-'}${body}`;
}

const parseDate = (iso: string): Date => {
  // Date-only strings are calendar dates: parse as local to avoid TZ shifts.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(iso);
};

/** "2026-06-28" → "28 jun" (es) · "28 Jun" (en) */
export function formatShortDate(iso: string): string {
  return parseDate(iso).toLocaleDateString(activeLocale(), { day: '2-digit', month: 'short' }).replace('.', '');
}

/** "lun, 22 sept" (es) · "Tue, 22 Sep" (en) */
export function formatLongDate(date: Date): string {
  return date.toLocaleDateString(activeLocale(), { weekday: 'short', day: '2-digit', month: 'short' }).replace(/\./g, '');
}

export function elapsedSince(startedAt: string, now: Date = new Date()): number {
  return Math.max(0, Math.floor((now.getTime() - new Date(startedAt).getTime()) / 1000));
}

/** "09:12" today · "Yesterday" · "12 sep" for thread lists */
export function formatRelativeTime(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (d.toDateString() === now.toDateString()) return formatTime(d);
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return i18n.t('common.yesterday');
  return formatShortDate(iso);
}
